import React, { useState, useEffect } from 'react';
import {
  Modal,
  Steps,
  Card,
  Button,
  Form,
  Input,
  Select,
  Typography,
  Alert,
  Space,
  Row,
  Col,
  Divider,
  Table,
  Checkbox,
  message,
  Radio,
  Spin
} from 'antd';
import {
  WhatsAppOutlined,
  PhoneOutlined,
  GlobalOutlined,
  DollarOutlined,
  UserOutlined,
  MailOutlined,
  FacebookOutlined,
  InfoCircleOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import axios from 'axios';
import axiosConfig from "../../../config/axiosConfig";

const { Title, Text, Paragraph } = Typography;
const { Step } = Steps;
const { Option } = Select;

// Custom hook for responsive detection
const useWindowSize = () => {
  const [windowSize, setWindowSize] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 0,
  });

  useEffect(() => {
    const handleResize = () => {
      setWindowSize({ width: window.innerWidth });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return windowSize;
};

const WhatsAppOnboardingModal = ({ visible, onClose, onComplete }) => {
  const { width } = useWindowSize();
  const isMobile = width <= 480;
  const isTablet = width <= 768;

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (visible) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [visible]);

  // Skip prerequisites - start directly at step 1 (Facebook Login)
  const [currentStep, setCurrentStep] = useState(1); // Changed from 0 to 1
  const [facebookSDKLoaded, setFacebookSDKLoaded] = useState(false);
  const [sdkError, setSdkError] = useState(null);
  const [signupData, setSignupData] = useState(null);
  const [embeddedData, setEmbeddedData] = useState(null);
  // Ref so callbacks (fbLoginCallback) always see the latest embeddedData
  // without stale closure issues
  const embeddedDataRef = React.useRef(null);
  // Prevent duplicate exchangeToken calls from both the message event and fbLoginCallback
  const exchangeCalledRef = React.useRef(false);
  const [isExchangingToken, setIsExchangingToken] = useState(false);
  const [tokenExchangeResponse, setTokenExchangeResponse] = useState(null);
  const [messageShown, setMessageShown] = useState(false);
  const [isSubscribingWebhooks, setIsSubscribingWebhooks] = useState(false);
  const [webhookSubscriptionResponse, setWebhookSubscriptionResponse] = useState(null);
  const [showPinStep, setShowPinStep] = useState(false);
  const [pin, setPin] = useState('');
  const [isRegisteringPhone, setIsRegisteringPhone] = useState(false);
  const [phoneRegistrationResponse, setPhoneRegistrationResponse] = useState(null);
  
  // Prerequisite states
  const [prerequisiteForm] = Form.useForm();
  const [prerequisitesVerified, setPrerequisitesVerified] = useState(false);
  const [prerequisiteData, setPrerequisiteData] = useState({
    website: '',
    facebookAccount: '',
    mobileNumber: ''
  });

  // Reset states when modal closes
  useEffect(() => {
    if (!visible) {
      setTokenExchangeResponse(null);
      setWebhookSubscriptionResponse(null);
      setSignupData(null);
      setEmbeddedData(null);
      embeddedDataRef.current = null;
      exchangeCalledRef.current = false;
      setMessageShown(false);
      setIsExchangingToken(false);
      setIsSubscribingWebhooks(false);
      setSdkError(null);
      setCurrentStep(1); // Reset to Facebook login step (skip prerequisites)
      setPrerequisitesVerified(false);
      prerequisiteForm.resetFields();
      setShowPinStep(false);
      setPin('');
      setIsRegisteringPhone(false);
      setPhoneRegistrationResponse(null);
    }
  }, [visible]);

  // Load Facebook SDK
  useEffect(() => {
    if (!window.FB) {
      // Load Facebook SDK
      const script = document.createElement('script');
      script.async = true;
      script.defer = true;
      script.crossOrigin = 'anonymous';
      script.src = 'https://connect.facebook.net/en_US/sdk.js';
      
      script.onload = () => {
        setFacebookSDKLoaded(true);
        setSdkError(null);
      };
      
      script.onerror = () => {
        setSdkError('Unable to load Facebook SDK. Please check your internet connection and try again.');
        setFacebookSDKLoaded(false);
      };
      
      document.body.appendChild(script);

      // SDK initialization
      window.fbAsyncInit = function() {
        try {
          window.FB.init({
            appId: '26595763293450585', // Your app ID
            autoLogAppEvents: true,
            xfbml: true,
            version: 'v24.0' // Graph API version
          });
          setFacebookSDKLoaded(true);
          setSdkError(null);
        } catch (error) {
          setSdkError('Failed to initialize Facebook SDK. Please refresh the page.');
          setFacebookSDKLoaded(false);
        }
      };

      // Session logging message event listener with error handling
      const handleMessage = (event) => {
       
        if (!event.origin.endsWith('facebook.com')) return;

        try {
          const data = JSON.parse(event.data);
          if (data.type === 'WA_EMBEDDED_SIGNUP') {
            console.log('WhatsApp Embedded Signup message received:', data);
            handleEmbeddedSignupMessage(data);
          }
        } catch {
          console.log('WhatsApp Embedded Signup message event: ', event.data);
          handleEmbeddedSignupMessage(event.data);
        }
      };
      
      window.addEventListener('message', handleMessage);
      
      // Cleanup
      return () => {
        window.removeEventListener('message', handleMessage);
      };
    } else {
      setFacebookSDKLoaded(true);
      setSdkError(null);
    }
  }, []);

  // Debug: Log embeddedData changes
  useEffect(() => {
    console.log('embeddedData state updated:', embeddedData);
  }, [embeddedData]);

  // Token exchange API call
  const exchangeToken = async (authCode, embeddedData = {}) => {
    try {
      setIsExchangingToken(true);
      console.log('🔄 Exchanging token with code:', authCode);
      console.log('📊 Embedded data passed to exchangeToken:', embeddedData);
      
      // IMPORTANT: Store passed embeddedData to state/ref immediately so subscribeWebhooks can access it
      if (Object.keys(embeddedData).length > 0) {
        embeddedDataRef.current = embeddedData;
        setEmbeddedData(embeddedData);
      }
      
      const response = await axiosConfig.get(`/embeddedsignup/exchangetoken?code=${authCode}`);
      
      console.log('✅ Token exchange response:', response.data);
      
      // Handle both success and error responses from the API
      if (response.data.success) {
        setTokenExchangeResponse(response.data);
        console.log('💾 Preparing WABA details insert with data:', embeddedData)
        // Insert WABA details into database now that we have all required data
        try {
          const wabaDetailsRequest = {
            phone_number_id: embeddedData?.phone_number_id || "",
            waba_id: embeddedData?.waba_id || "",
            access_token: response.data?.data?.access_token || response.data?.data?.BUSINESS_TOKEN || "",
            phone_number: embeddedData?.phone_number || "",
            business_token: response.data?.data?.BUSINESS_TOKEN || "",
            waba_status: embeddedData?.waba_status || "active",
            messaging_limit: embeddedData?.messaging_limit || "1000",
            quality_rating: embeddedData?.quality_rating || "GREEN",
            code_verification_status: embeddedData?.code_verification_status || 1,
            verified_name: embeddedData?.verified_name || "",
            account_review_status: embeddedData?.account_review_status || 1,
            ownership_type: embeddedData?.ownership_type || "SELF_OWNED",
            message_template_namespace: embeddedData?.message_template_namespace || "",
            access_token_type: response.data?.data?.access_token_type || "Temporary",
            business_name: embeddedData?.business_name || "",
            mobile_no: embeddedData?.phone_number || ""
          };
          
          console.log('📤 WABA Details Request being sent:', wabaDetailsRequest);
          
          const wabaResponse = await axiosConfig.post('/embeddedsignup/insert-waba-details', wabaDetailsRequest);
          
          if (wabaResponse.data.success) {
            console.log('✅ WABA details inserted successfully:', wabaResponse.data);
          } else {
            console.warn('⚠️ WABA details insertion failed:', wabaResponse.data);
          }
        } catch (error) {
          console.error('Error inserting WABA details:', error);
          // Don't fail the entire process if WABA details insertion fails
        }
        
        // After token exchange succeeds, proceed to webhook subscription with the embedded data
        // Pass BOTH embedded data AND token response so subscribeWebhooks doesn't rely on state
        console.log('🔗 Token exchange complete, calling subscribeWebhooks...');
        subscribeWebhooks(embeddedData, response.data).catch(error => {
          console.error('Webhook subscription failed:', error);
        });
      } else {
        // Handle API error response
        const errorMessage = response.data.message || 'Token exchange failed';
        setTokenExchangeResponse({
          success: false,
          error: errorMessage
        });
      }
    } catch (error) {
      console.error('Token exchange error:', error);
      
      // Extract meaningful error message
      let errorMessage = 'Connection failed. Please try again.';
      if (error.response?.status === 400) {
        errorMessage = 'Invalid authorization code. Please try again.';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setTokenExchangeResponse({
        success: false,
        error: errorMessage
      });
    } finally {
      setIsExchangingToken(false);
    }
  };

  // Subscribe to webhooks API call
  const subscribeWebhooks = async (passedEmbeddedData = null, passedTokenResponse = null) => {
    try {
      setIsSubscribingWebhooks(true);
      
      // Use passed data or fall back to state
      const dataToUse = passedEmbeddedData || embeddedData;
      const tokenDataToUse = passedTokenResponse || tokenExchangeResponse;
      
      console.log('Subscribing to webhooks...');
      console.log('Using embedded data:', dataToUse);
      console.log('Using token response:', tokenDataToUse);
      
      // Get all required data from passed or stored data
      const wabaId = dataToUse?.waba_id;
      const phoneNumberId = dataToUse?.phone_number_id;
      const businessToken = tokenDataToUse?.data?.BUSINESS_TOKEN;
      const phoneNumber = dataToUse?.phone_number;
      const accessToken = tokenDataToUse?.data?.access_token || tokenDataToUse?.data?.BUSINESS_TOKEN;
      
      console.log('🔓 Extracted values from embedded data:', { wabaId, phoneNumberId, businessToken: businessToken ? 'present' : 'missing' });
      
      if (!wabaId || !businessToken) {
        throw new Error(`Missing required fields: WABA ID=${wabaId}, Business Token=${businessToken ? 'present' : 'missing'}`);
      }

      const requestData = {
        waba_id: wabaId,
        business_token: businessToken,
        customeruuid: " ", // Blank string as requested
        phone_number: phoneNumber || "",
        phone_number_id: phoneNumberId || "",
        access_token: accessToken || ""
      };

      console.log('📨 Webhook subscription request:', requestData);
      
      const response = await axiosConfig.post('/embeddedsignup/subscribe-webhooks', requestData);
      
      if (response.data.success) {
        setWebhookSubscriptionResponse(response.data);
        // Webhooks subscribed → move to PIN registration step
        message.success('WhatsApp account connected! Please verify your phone number.');
        console.log('Webhook subscription response:', response.data);
        setShowPinStep(true);
      } else {
        throw new Error(response.data.message || 'Webhook subscription failed');
      }
    } catch (error) {
      console.error('Webhook subscription error:', error);
      message.error(`Webhook subscription failed: ${error.message}`);
      setWebhookSubscriptionResponse({
        success: false,
        error: error.message
      });
    } finally {
      setIsSubscribingWebhooks(false);
    }
  };

  // Handle embedded signup messages
  // Register phone number with 6-digit PIN
  const registerPhone = async (pinValue) => {
    try {
      setIsRegisteringPhone(true);
      const response = await axiosConfig.post('/embeddedsignup/register-phone', { pin: pinValue });
      if (response.data.success) {
        setPhoneRegistrationResponse(response.data);
        message.success('🎉 Your WhatsApp Business Account is now live and ready to use!');
        onComplete({
          signupData,
          embeddedData,
          tokenData: tokenExchangeResponse,
          webhookData: webhookSubscriptionResponse,
          registrationData: response.data
        });
      } else {
        throw new Error(response.data.message || 'Phone registration failed');
      }
    } catch (error) {
      console.error('Phone registration error:', error);
      const errMsg = error.response?.data?.message || error.message || 'Phone registration failed';
      message.error(`Verification failed: ${errMsg}`);
      setPhoneRegistrationResponse({ success: false, error: errMsg });
    } finally {
      setIsRegisteringPhone(false);
    }
  };

  const handleEmbeddedSignupMessage = (data) => {
    // Prevent multiple messages for the same response
    if (messageShown) return;
    
    if (data.event === 'FINISH' || data.event === 'FINISH_ONLY_WABA' || data.event === 'FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING') {
      setMessageShown(true);
      // Successful completion
      setSignupData(data);
      
      // Extract and store embedded signup data
      const embData = data.data || {};
      embeddedDataRef.current = embData;
      setEmbeddedData(embData);
      
      // Log for debugging - DETAILED
      console.log('🎉 WhatsApp Business Account Data (FINISH event):', data);
      console.log('📦 Extracted Embedded Data:', embData);
      console.log('🔑 WABA ID:', embData?.waba_id);
      console.log('📱 Phone Number ID:', embData?.phone_number_id);
      console.log('✅ Auth Code:', data.auth_code);
      
      // Check if we have auth code in the signup data and exchange token
      if (data.auth_code) {
        console.log('🚀 Calling exchangeToken with auth code and embedded data...');
        exchangeCalledRef.current = true;
        exchangeToken(data.auth_code, embData).catch(error => {
          console.error('Token exchange failed:', error);
        });
      } else {
        // If no auth code in signup data, show warning but don't complete
        console.warn('No auth code found in signup data');
        message.warning('WhatsApp account created but missing auth code. Please try again.');
      }
      
      // Reset message flag after a delay
      setTimeout(() => setMessageShown(false), 3000);
    } else if (data.event === 'CANCEL') {
      // User cancelled or encountered error
      if (data.data?.error_message) {
        message.error(`Error: ${data.data.error_message}`);
        console.error('WhatsApp Embedded Signup Error:', data);
      } else {
        message.warning('WhatsApp Business Account setup was cancelled');
        console.log('WhatsApp Embedded Signup Cancelled at:', data.data?.current_step);
      }
    }
  };

  // Response callback
  const fbLoginCallback = (response) => {
    // Prevent multiple messages for the same response
    if (messageShown) return;
    setMessageShown(true);
    
    if (response.authResponse && response.authResponse.code) {
      const code = response.authResponse.code;
      console.log('Facebook Auth Code received:', code);
      
      // Use the ref so we always have the latest embeddedData (avoids stale closure)
      // Also skip if handleEmbeddedSignupMessage already initiated the exchange
      if (exchangeCalledRef.current) {
        console.log('⏭️ exchangeToken already called from message event, skipping duplicate call from fbLoginCallback');
      } else {
        exchangeCalledRef.current = true;
        exchangeToken(code, embeddedDataRef.current || {}).catch(error => {
          console.error('Token exchange failed:', error);
        });
      }
    } else {
      console.log('Facebook Auth Response:', response);
      message.error('Facebook authentication failed. Please try again.');
    }
    
    // Reset message flag after a delay
    setTimeout(() => setMessageShown(false), 3000);
  };

  // Launch WhatsApp Embedded Signup
  const launchWhatsAppSignup = () => {
    if (!window.FB) {
      message.error('Facebook SDK not loaded. Please refresh the page.');
      return;
    }

    // Reset states before starting new signup (but NOT embeddedData as it will be set by the message handler)
    setSignupData(null);
    setTokenExchangeResponse(null);
    setWebhookSubscriptionResponse(null);
    setMessageShown(false);

    window.FB.login(fbLoginCallback, {
      config_id: '1481174170053665', // You'll need to get this from Facebook
      response_type: 'code',
      override_default_response_type: true,
      extras: {
        setup: {},
        featureType: '', // Leave blank for default flow
        sessionInfoVersion: '3',
      }
    });
  };

  const steps = [];

  // Prerequisite validation functions
  const validateWebsite = (website) => {
    try {
      new URL(website);
      return true;
    } catch {
      return false;
    }
  };

  const validateMobileNumber = (phoneNumber) => {
    // Check if it's a valid mobile number format (minimum 10 digits)
    const phoneRegex = /^[0-9]{10,15}$/;
    return phoneRegex.test(phoneNumber.replace(/\D/g, ''));
  };

  const handlePrerequisiteSubmit = async (values) => {
    // Validate website URL
    if (!validateWebsite(values.website)) {
      message.error('Please enter a valid website URL (e.g., https://example.com)');
      return;
    }

    // Validate mobile number
    if (!validateMobileNumber(values.mobileNumber)) {
      message.error('Please enter a valid mobile number (10-15 digits)');
      return;
    }

    // Validate Facebook account/page
    if (!values.facebookAccount || values.facebookAccount.trim() === '') {
      message.error('Please enter your Facebook account or page name');
      return;
    }

    // All prerequisites verified
    setPrerequisiteData({
      website: values.website,
      facebookAccount: values.facebookAccount,
      mobileNumber: values.mobileNumber
    });
    setPrerequisitesVerified(true);
    message.success('✅ All prerequisites verified! Proceeding to WhatsApp setup...');
    
    // Move to next step (Facebook login)
    setTimeout(() => {
      setCurrentStep(1);
    }, 500);
  };





  const handleFacebookLogin = () => {
    if (sdkError) {
      message.error(sdkError);
      return;
    }
    
    if (!facebookSDKLoaded) {
      message.error('Facebook SDK is still loading. Please try again in a moment.');
      return;
    }
    
    if (!window.FB) {
      message.error('Facebook SDK not available. Please refresh the page and try again.');
      return;
    }

    // Launch WhatsApp Embedded Signup
    launchWhatsAppSignup();
  };

  const renderStepContent = () => {
    // Step 0: Prerequisites
    if (currentStep === 0) {
      return (
        <div style={{ maxWidth: isMobile ? '100%' : 650, margin: '0 auto', padding: isMobile ? '8px' : '10px 20px' }}>
          {/* Compact Header */}
          <div style={{ textAlign: 'center', marginBottom: isMobile ? 20 : 28 }}>
            <Title level={3} style={{ margin: '0 0 8px 0', color: '#0f172a', fontWeight: 700, fontSize: isMobile ? 18 : 24 }}>
              Prerequisites Check
            </Title>
            <Text type="secondary" style={{ fontSize: isMobile ? 13 : 14 }}>
              Verify you have these 3 items before proceeding
            </Text>
          </div>

          {/* Compact Prerequisites List */}
          <div style={{ marginBottom: isMobile ? 20 : 28, display: 'grid', gridTemplateColumns: '1fr', gap: isMobile ? 10 : 12 }}>
            <div style={{
              background: '#f0fdf4',
              border: '1px solid #bbf7d0',
              borderRadius: 8,
              padding: isMobile ? '10px 12px' : '12px 16px',
              display: 'flex',
              alignItems: 'center',
              gap: isMobile ? 10 : 12
            }}>
              <CheckCircleOutlined style={{ fontSize: isMobile ? 18 : 20, color: '#16a34a', flexShrink: 0 }} />
              <div>
                <Text strong style={{ color: '#166534', fontSize: isMobile ? 12 : 13, display: 'block' }}>
                  Valid Website
                </Text>
                <Text style={{ color: '#15803d', fontSize: isMobile ? 11 : 12 }}>
                  Your business website URL
                </Text>
              </div>
            </div>

            <div style={{
              background: '#f0fdf4',
              border: '1px solid #bbf7d0',
              borderRadius: 8,
              padding: isMobile ? '10px 12px' : '12px 16px',
              display: 'flex',
              alignItems: 'center',
              gap: isMobile ? 10 : 12
            }}>
              <CheckCircleOutlined style={{ fontSize: isMobile ? 18 : 20, color: '#16a34a', flexShrink: 0 }} />
              <div>
                <Text strong style={{ color: '#166534', fontSize: isMobile ? 12 : 13, display: 'block' }}>
                  Facebook Account / Page
                </Text>
                <Text style={{ color: '#15803d', fontSize: isMobile ? 11 : 12 }}>
                  Active Facebook account with admin access
                </Text>
              </div>
            </div>

            <div style={{
              background: '#f0fdf4',
              border: '1px solid #bbf7d0',
              borderRadius: 8,
              padding: isMobile ? '10px 12px' : '12px 16px',
              display: 'flex',
              alignItems: 'center',
              gap: isMobile ? 10 : 12
            }}>
              <CheckCircleOutlined style={{ fontSize: isMobile ? 18 : 20, color: '#16a34a', flexShrink: 0 }} />
              <div>
                <Text strong style={{ color: '#166534', fontSize: isMobile ? 12 : 13, display: 'block' }}>
                  Fresh Mobile Number
                </Text>
                <Text style={{ color: '#15803d', fontSize: isMobile ? 11 : 12 }}>
                  Not yet registered on WhatsApp
                </Text>
              </div>
            </div>
          </div>

          {/* Form - Compact Layout */}
          <Form
            form={prerequisiteForm}
            layout="vertical"
            onFinish={handlePrerequisiteSubmit}
            requiredMark={false}
          >
            <Form.Item
              label={<Text style={{ fontSize: isMobile ? 12 : 13, fontWeight: 500 }}>Website URL</Text>}
              name="website"
              rules={[
                { required: true, message: 'Website required' },
                {
                  pattern: /^https?:\/\/.+\..+/,
                  message: 'Enter valid URL (e.g., https://example.com)'
                }
              ]}
              style={{ marginBottom: isMobile ? 10 : 12 }}
            >
              <Input
                placeholder="https://yourcompany.com"
                prefix={<GlobalOutlined style={{ fontSize: isMobile ? 13 : 14 }} />}
                size={isMobile ? 'middle' : 'middle'}
                style={{ borderRadius: 6, fontSize: isMobile ? 13 : 13 }}
              />
            </Form.Item>

            <Form.Item
              label={<Text style={{ fontSize: isMobile ? 12 : 13, fontWeight: 500 }}>Facebook Account / Page</Text>}
              name="facebookAccount"
              rules={[
                { required: true, message: 'Facebook account required' },
                { min: 2, message: 'Enter valid Facebook account name' }
              ]}
              style={{ marginBottom: isMobile ? 10 : 12 }}
            >
              <Input
                placeholder="Your Facebook page name"
                prefix={<FacebookOutlined style={{ fontSize: isMobile ? 13 : 14 }} />}
                size={isMobile ? 'middle' : 'middle'}
                style={{ borderRadius: 6, fontSize: isMobile ? 13 : 13 }}
              />
            </Form.Item>

            <Form.Item
              label={<Text style={{ fontSize: isMobile ? 12 : 13, fontWeight: 500 }}>Mobile Number (Not on WhatsApp)</Text>}
              name="mobileNumber"
              rules={[
                { required: true, message: 'Mobile number required' },
                {
                  pattern: /^[0-9\s\-\+\(\)]{10,15}$/,
                  message: 'Enter valid mobile (10-15 digits)'
                }
              ]}
              style={{ marginBottom: isMobile ? 12 : 16 }}
            >
              <Input
                placeholder="+1 234 567 8900"
                prefix={<PhoneOutlined style={{ fontSize: isMobile ? 13 : 14 }} />}
                size={isMobile ? 'middle' : 'middle'}
                style={{ borderRadius: 6, fontSize: isMobile ? 13 : 13 }}
              />
            </Form.Item>

            <Form.Item style={{ marginBottom: 0 }}>
              <Button
                type="primary"
                htmlType="submit"
                block
                style={{
                  height: isMobile ? 38 : 40,
                  fontSize: isMobile ? 13 : 14,
                  fontWeight: 600,
                  borderRadius: 6,
                  background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)',
                  border: 'none'
                }}
              >
                ✓ Verify & Continue
              </Button>
            </Form.Item>
          </Form>
        </div>
      );
    }

    // Step 1: Facebook Login (original step 4 content)
    if (currentStep === 1) {
      return (
      <div style={{ maxWidth: isMobile ? '100%' : '100%', margin: '0 auto', padding: isMobile ? '4px' : '10px 30px' }}>
        {/* Header Section */}
        <div style={{ textAlign: 'center', marginBottom: isMobile ? 16 : 24 }}>
          <div style={{
            width: isMobile ? 48 : 64,
            height: isMobile ? 48 : 64,
            margin: isMobile ? '0 auto 10px' : '0 auto 16px',
            background: 'linear-gradient(135deg, #1877f2 0%, #0a66c2 100%)',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 8px 24px rgba(24, 119, 242, 0.3)'
          }}>
            <FacebookOutlined style={{ fontSize: isMobile ? 24 : 32, color: 'white' }} />
          </div>
          
          <Title level={2} style={{ margin: '0 0 6px 0', color: '#0f172a', fontWeight: 700, fontSize: isMobile ? 18 : 24 }}>
            Connect Your Account
          </Title>
          <Text type="secondary" style={{ fontSize: isMobile ? 13 : 14, lineHeight: 1.4 }}>
            Complete WhatsApp Business setup in one simple step
          </Text>
        </div>

        {/* SDK Error Message */}
        {sdkError && (
          <div style={{
            background: '#fef2f2',
            border: '2px solid #ef4444',
            borderRadius: isMobile ? 10 : 12,
            padding: isMobile ? 16 : 20,
            textAlign: 'center',
            marginBottom: isMobile ? 16 : 24
          }}>
            <span style={{ fontSize: isMobile ? 28 : 32, display: 'block', marginBottom: isMobile ? 8 : 12 }}>📡</span>
            <Text strong style={{ color: '#991b1b', fontSize: isMobile ? 14 : 16, display: 'block', marginBottom: 8 }}>
              Connection Issue
            </Text>
            <Text style={{ color: '#7f1d1d', fontSize: isMobile ? 13 : 14, display: 'block', marginBottom: isMobile ? 12 : 16 }}>
              {sdkError}
            </Text>
            <Button
              type="primary"
              danger
              size={isMobile ? 'middle' : 'large'}
              onClick={() => window.location.reload()}
              style={{
                width: '100%',
                height: isMobile ? 40 : 44,
                borderRadius: 8,
                fontSize: isMobile ? 14 : 15,
                fontWeight: 600
              }}
            >
              🔄 Refresh Page
            </Button>
          </div>
        )}

        {/* Main CTA Card - Show only if no signup data yet and no SDK error */}
        {!signupData && !sdkError && (
          <Card 
            style={{ 
              marginBottom: isMobile ? 12 : 16,
              background: 'linear-gradient(135deg, #1877f2 0%, #0a66c2 100%)',
              border: 'none',
              color: 'white',
              boxShadow: '0 12px 40px rgba(24, 119, 242, 0.25)',
              borderRadius: isMobile ? 10 : 12
            }}
          >
            <div style={{ textAlign: 'center', padding: isMobile ? '12px 10px' : '18px 16px' }}>
              <Title level={4} style={{ color: 'white', margin: '0 0 6px 0', fontSize: isMobile ? 15 : 17 }}>
                Ready to Begin?
              </Title>
              <Text style={{ color: 'rgba(255,255,255,0.95)', fontSize: isMobile ? 12 : 13, lineHeight: 1.4 }}>
                Connect your Facebook Business account to setup WhatsApp messaging in minutes
              </Text>

              {!facebookSDKLoaded && (
                <div style={{ marginTop: isMobile ? 10 : 12, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                  <Spin size="small" style={{ color: 'white' }} />
                  <Text style={{ color: 'rgba(255,255,255,0.9)', fontSize: isMobile ? 12 : 13 }}>
                    Loading...
                  </Text>
                </div>
              )}

              {facebookSDKLoaded && (
                <Button
                  type="primary"
                  size={isMobile ? 'middle' : 'middle'}
                  icon={<FacebookOutlined />}
                  onClick={handleFacebookLogin}
                  loading={isExchangingToken || isSubscribingWebhooks}
                  disabled={isExchangingToken || isSubscribingWebhooks}
                  style={{
                    marginTop: isMobile ? 10 : 12,
                    backgroundColor: 'white',
                    color: '#1877f2',
                    borderColor: 'white',
                    fontWeight: 600,
                    height: isMobile ? 38 : 40,
                    fontSize: isMobile ? 13 : 14,
                    width: '100%',
                    borderRadius: 8
                  }}
                >
                  {isExchangingToken
                    ? '⏳ Processing...'
                    : isSubscribingWebhooks
                      ? '🔄 Setting up...'
                      : '🔐 Continue with Facebook'
                  }
                </Button>
              )}
            </div>
          </Card>
        )}

        {/* Response Messages */}
        {tokenExchangeResponse && tokenExchangeResponse.success !== undefined && (
          <div style={{ marginBottom: isMobile ? 16 : 24 }}>
            {tokenExchangeResponse.success ? (
              <div style={{
                background: 'linear-gradient(135deg, #f0fdf4 0%, #dbeafe 100%)',
                border: '2px solid #22c55e',
                borderRadius: isMobile ? 10 : 12,
                padding: isMobile ? 16 : 20,
                textAlign: 'center'
              }}>
                <span style={{ fontSize: isMobile ? 28 : 32, display: 'block', marginBottom: isMobile ? 8 : 12 }}>✅</span>
                <Text strong style={{ color: '#166534', fontSize: isMobile ? 14 : 16, display: 'block', marginBottom: 8 }}>
                  Token Received!
                </Text>
                <Text style={{ color: '#15803d', fontSize: isMobile ? 13 : 14, display: 'block', marginBottom: isMobile ? 12 : 16 }}>
                  Your Facebook authentication was successful
                </Text>

                {!webhookSubscriptionResponse && (
                  <Button
                    type="primary"
                    size={isMobile ? 'middle' : 'large'}
                    onClick={subscribeWebhooks}
                    loading={isSubscribingWebhooks}
                    disabled={isSubscribingWebhooks}
                    style={{
                      background: '#16a34a',
                      borderColor: '#16a34a',
                      width: '100%',
                      height: isMobile ? 40 : 44,
                      borderRadius: 8,
                      fontSize: isMobile ? 14 : 15,
                      fontWeight: 600
                    }}
                  >
                    {isSubscribingWebhooks ? '⏳ Activating...' : '🚀 Go Live'}
                  </Button>
                )}
              </div>
            ) : (
              <div style={{
                background: '#fef2f2',
                border: '2px solid #ef4444',
                borderRadius: isMobile ? 10 : 12,
                padding: isMobile ? 16 : 20,
                textAlign: 'center'
              }}>
                <span style={{ fontSize: isMobile ? 28 : 32, display: 'block', marginBottom: isMobile ? 8 : 12 }}>⚠️</span>
                <Text strong style={{ color: '#991b1b', fontSize: isMobile ? 14 : 16, display: 'block', marginBottom: 8 }}>
                  Connection Failed
                </Text>
                <Text style={{ color: '#7f1d1d', fontSize: isMobile ? 13 : 14, display: 'block', marginBottom: isMobile ? 12 : 16 }}>
                  {tokenExchangeResponse.error || 'Please try again or check your credentials'}
                </Text>
                <Button
                  type="primary"
                  danger
                  size={isMobile ? 'middle' : 'large'}
                  onClick={handleFacebookLogin}
                  loading={isExchangingToken}
                  style={{
                    width: '100%',
                    height: isMobile ? 40 : 44,
                    borderRadius: 8,
                    fontSize: isMobile ? 14 : 15,
                    fontWeight: 600
                  }}
                >
                  🔄 Try Again
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Webhook error state */}
        {webhookSubscriptionResponse && !webhookSubscriptionResponse.success && (
          <div style={{ marginBottom: isMobile ? 16 : 24 }}>
            <div style={{
              background: '#fef2f2',
              border: '2px solid #ef4444',
              borderRadius: isMobile ? 10 : 12,
              padding: isMobile ? 16 : 20,
              textAlign: 'center'
            }}>
              <span style={{ fontSize: isMobile ? 28 : 32, display: 'block', marginBottom: isMobile ? 8 : 12 }}>❌</span>
              <Text strong style={{ color: '#991b1b', fontSize: isMobile ? 14 : 16, display: 'block', marginBottom: 8 }}>
                Webhook Setup Failed
              </Text>
              <Text style={{ color: '#7f1d1d', fontSize: isMobile ? 13 : 14, display: 'block', marginBottom: isMobile ? 12 : 16 }}>
                {webhookSubscriptionResponse.error || 'Failed to configure webhooks. Please try again.'}
              </Text>
            </div>
          </div>
        )}

        {/* PIN Entry Step — shown after webhooks subscribed */}
        {showPinStep && !phoneRegistrationResponse?.success && (
          <div style={{ marginBottom: isMobile ? 16 : 24 }}>
            <div style={{
              background: 'linear-gradient(135deg, #f0fdf4 0%, #dbeafe 100%)',
              border: '2px solid #22c55e',
              borderRadius: isMobile ? 10 : 12,
              padding: isMobile ? 20 : 28,
              textAlign: 'center'
            }}>
              <span style={{ fontSize: isMobile ? 28 : 32, display: 'block', marginBottom: isMobile ? 8 : 12 }}>📱</span>
              <Text strong style={{ color: '#166534', fontSize: isMobile ? 15 : 17, display: 'block', marginBottom: 6 }}>
                Verify Your Phone Number
              </Text>
              <Text style={{ color: '#15803d', fontSize: isMobile ? 12 : 13, display: 'block', marginBottom: isMobile ? 16 : 20 }}>
                Enter the 6-digit verification PIN sent to your registered phone number.
              </Text>
              <Input.OTP
                length={6}
                value={pin}
                onChange={(val) => setPin(val)}
                style={{ marginBottom: isMobile ? 12 : 16 }}
                disabled={isRegisteringPhone}
              />
              <Button
                type="primary"
                block
                size={isMobile ? 'middle' : 'large'}
                onClick={() => registerPhone(pin)}
                loading={isRegisteringPhone}
                disabled={isRegisteringPhone || pin.length !== 6}
                style={{
                  marginTop: isMobile ? 8 : 12,
                  background: '#16a34a',
                  borderColor: '#16a34a',
                  height: isMobile ? 40 : 44,
                  borderRadius: 8,
                  fontSize: isMobile ? 14 : 15,
                  fontWeight: 600
                }}
              >
                {isRegisteringPhone ? '⏳ Verifying...' : '✓ Verify & Activate'}
              </Button>
              {phoneRegistrationResponse && !phoneRegistrationResponse.success && (
                <Text type="danger" style={{ display: 'block', marginTop: 8, fontSize: isMobile ? 12 : 13 }}>
                  {phoneRegistrationResponse.error || 'Verification failed. Please check your PIN and try again.'}
                </Text>
              )}
            </div>
          </div>
        )}

        {/* Final success — shown after phone registered */}
        {phoneRegistrationResponse?.success && (
          <div style={{ marginBottom: isMobile ? 16 : 24 }}>
            <div style={{
              background: 'linear-gradient(135deg, #f0fdf4 0%, #dbeafe 100%)',
              border: '2px solid #22c55e',
              borderRadius: isMobile ? 10 : 12,
              padding: isMobile ? 20 : 32,
              textAlign: 'center'
            }}>
              <div style={{
                width: isMobile ? 48 : 60,
                height: isMobile ? 48 : 60,
                margin: isMobile ? '0 auto 12px' : '0 auto 16px',
                background: 'white',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 12px rgba(34, 197, 94, 0.2)'
              }}>
                <span style={{ fontSize: isMobile ? 28 : 32 }}>🎉</span>
              </div>
              <Text strong style={{ color: '#166534', fontSize: isMobile ? 16 : 18, display: 'block', marginBottom: 8 }}>
                All Set!
              </Text>
              <Text style={{ color: '#15803d', fontSize: isMobile ? 14 : 15, display: 'block', marginBottom: isMobile ? 16 : 20, lineHeight: 1.6 }}>
                Your WhatsApp Business Account is now live and ready to send messages.
              </Text>
              <div style={{ background: 'white', padding: isMobile ? 12 : 16, borderRadius: 8, border: '1px solid #bbf7d0' }}>
                <Text style={{ fontSize: isMobile ? 12 : 13, color: '#065f46', display: 'block' }}>
                  ✓ Account connected<br/>
                  ✓ Webhooks configured<br/>
                  ✓ Phone number registered
                </Text>
              </div>
            </div>
          </div>
        )}

        {signupData && !webhookSubscriptionResponse && (
          <div style={{
            background: '#e0f2fe',
            border: '2px solid #0284c7',
            borderRadius: isMobile ? 10 : 12,
            padding: isMobile ? 12 : 16,
            textAlign: 'center'
          }}>
            <Text strong style={{ color: '#0c4a6e', display: 'block', marginBottom: 4, fontSize: isMobile ? 13 : 15 }}>
              ✓ Account Details Received
            </Text>
            <Text type="secondary" style={{ fontSize: isMobile ? 11 : 12, color: '#0c4a6e' }}>
              Preparing final setup...
            </Text>
          </div>
        )}

        {/* Footer Help */}
        <div style={{ textAlign: 'center', marginTop: isMobile ? 12 : 16, paddingTop: isMobile ? 10 : 12, borderTop: '1px solid #e5e7eb' }}>
          <Text type="secondary" style={{ fontSize: isMobile ? 11 : 12 }}>
            Questions? Contact{' '}
            <a href="mailto:support@messageos.com" style={{ color: '#1890ff', textDecoration: 'none', fontWeight: 500 }}>
              support@messageos.com
            </a>
          </Text>
        </div>
      </div>
      );
    }
  };

  return (
    <Modal
      title={
        <div style={{ textAlign: 'center' }}>
          <WhatsAppOutlined style={{ color: '#25D366', fontSize: isMobile ? 20 : 24, marginRight: 8 }} />
          <span style={{ fontSize: isMobile ? 14 : 18, fontWeight: 600 }}>WhatsApp Business Setup</span>
        </div>
      }
      open={visible}
      onCancel={onClose}
      footer={null}
      width={isMobile ? '95%' : isTablet ? '85%' : 700}
      style={{ 
        top: 'auto',
        paddingBottom: isMobile ? 20 : 40,
      }}
      className="whatsapp-onboarding-modal"
      destroyOnClose
      bodyStyle={{
        padding: isMobile ? '16px' : '24px',
        overflow: 'hidden'
      }}
      centered
      maskStyle={{
        overflow: 'hidden'
      }}
    >
      {/* Hide steps since we're skipping to step 1 */}
      {/* <Steps 
        current={currentStep} 
        style={{ marginBottom: isMobile ? 16 : 32 }}
        size="small"
        direction={isMobile ? 'horizontal' : 'horizontal'}
      >
        {steps.map((step, index) => (
          <Step 
            key={index}
            title={isMobile ? '' : step.title} 
            description={isTablet ? '' : step.description}
            icon={step.icon}
          />
        ))}
      </Steps> */}

      {renderStepContent()}
    </Modal>
  );
};

export default WhatsAppOnboardingModal;